<template>
  <div
    class="flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full cursor-pointer"
    @click="goToProfile"
  >
    <!-- Portrait photo -->
    <div class="aspect-[3/4] bg-gray-100 overflow-hidden">
      <PersonPictureSet
        :person="person"
        :alt="person.name"
        class="picture w-full h-full"
      />
    </div>

    <!-- Card body -->
    <div class="p-3 flex flex-col gap-1.5 flex-1">
      <!-- Name -->
      <NuxtLink
        class="font-bold text-primary text-sm truncate hover:underline"
        :to="professionalLink"
        target="_blank"
        rel="noopener noreferrer"
        @click.native.stop
      >
        {{ person.name }}
      </NuxtLink>

      <!-- Profession + Rating -->
      <div class="flex items-center gap-1">
        <IconComponent
          icon="briefcase"
          class="text-gray-400 w-3 flex-shrink-0"
        />
        <span class="text-xs text-gray-500 truncate">{{ professionName }}</span>
      </div>
      <RatingIndicator :score="person.score" class="text-xs" />

      <!-- Short bio -->
      <p v-if="person.memo" class="text-xs text-gray-600 line-clamp-3 flex-1">
        {{ person.memo.slice(0, 200) }}
      </p>

      <!-- Online profession -->
      <div
        v-if="isOnlineProfession"
        class="flex flex-col gap-1 text-xs text-gray-500"
      >
        <span class="flex items-center gap-1">
          <IconComponent
            icon="globe"
            class="text-primary-light w-3 flex-shrink-0"
          />
          {{ $t("personProfile.location.onlineCoverage.cardOnline") }}
        </span>
        <span
          v-if="showInPersonConsultation && person.city?.name"
          class="flex items-center gap-1"
        >
          <IconComponent
            icon="map-marker-alt"
            class="text-red-500 w-3 flex-shrink-0"
          />
          {{
            $t("personProfile.location.onlineCoverage.cardInPerson", {
              city: person.city.name,
            })
          }}
        </span>
      </div>

      <!-- Location -->
      <div
        v-else-if="person.city && showCity"
        class="flex items-center gap-1 text-xs text-gray-500"
      >
        <IconComponent icon="check" class="text-success w-3 flex-shrink-0" />
        <span>{{ person.city.name }}</span>
      </div>

      <!-- Stats row -->
      <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
        <span v-if="person.winCount > 0" class="flex items-center gap-0.5">
          <IconComponent icon="check" class="text-success w-3" />
          {{ $t("personCard.comission", { count: person.winCount }) }}
        </span>
        <span v-if="person.scoreCount > 0" class="flex items-center gap-0.5">
          <IconComponent icon="star" class="text-yellow-400 w-3" />
          {{ $t("personCard.opinion", { count: person.scoreCount }) }}
        </span>
        <span v-if="person.answerCount > 0" class="flex items-center gap-0.5">
          <IconComponent icon="comments" class="text-primary w-3" />
          {{ $t("personCard.answers", { count: person.answerCount }) }}
        </span>
        <span v-if="photoCount > 0" class="flex items-center gap-0.5">
          <IconComponent icon="camera" class="text-primary-darkest w-3" />
          {{ $t("personCard.referencePhotos", { count: photoCount }) }}
        </span>
      </div>

      <!-- Phone -->
      <JoszakiButton
        v-if="phoneNumber && phoneNumber.number"
        class="text-sm"
        expanded
        inverted
        outline
        icon-left="phone-alt"
        icon-left-class="flex-shrink-0"
        :href="phoneLink"
        @click.stop.prevent="onClickPhone"
      >
        <span class="truncate min-w-0 text-sm">{{ formattedPhone }}</span>
      </JoszakiButton>

      <!-- CTA -->
      <JoszakiButton
        :to="hasRequestTenderListener ? undefined : tenderLink"
        target="_blank"
        class="text-sm"
        expanded
        inverted
        outline
        rel="noopener noreferrer"
        @click.stop="onClickCta"
      >
        {{ $t("landing.professionGroups.requestOffer") }}
      </JoszakiButton>
    </div>
  </div>
</template>

<script>
import { computed, useContext } from "@nuxtjs/composition-api";
import PersonPictureSet from "~/components/PersonPictureSet.vue";
import RatingIndicator from "~/components/_refactored/rating/RatingIndicator.vue";
import {
  isOnlineProfessionEnabled,
  isInPersonConsultationVisible,
} from "~/experiments/onlineProfession";

export default {
  name: "ProfessionalPortraitCard",
  components: {
    PersonPictureSet,
    RatingIndicator,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    professionName: {
      type: String,
      default: "",
    },
    highlightedProfession: {
      type: Object,
      default: null,
    },
    highlightedCity: {
      type: Object,
      default: null,
    },
    showCity: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit, listeners }) {
    const { localePath, $helpers, $ab } = useContext();

    const isOnlineProfession = computed(() => {
      // Gated behind the PostHog feature flag (variant1 or variant2).
      if (!isOnlineProfessionEnabled($ab)) {
        return false;
      }
      if (
        props.highlightedProfession &&
        props.highlightedProfession.online != null
      ) {
        return props.highlightedProfession.online;
      }
      const professions = props.person?.professions ?? [];
      return professions.length > 0 && professions.every((p) => p.online);
    });

    // The "Személyes konzultáció: {city}" line is only shown in variant2.
    const showInPersonConsultation = computed(() =>
      isInPersonConsultationVisible($ab)
    );

    // When a parent listens for these events (e.g. the profession-city page
    // A/B test), defer navigation/dialog handling to the parent so tracking and
    // the in-page tender dialog match the control CardPerson exactly. With no
    // listeners (front page), the card keeps its standalone navigation.
    const hasRequestTenderListener = computed(() => !!listeners.requestTender);

    const professionalLink = computed(() => {
      const basePath = localePath({
        name: "professionalPage",
        params: { professionalSeoName: props.person.seoName },
      });

      const hashParams = [];
      if (props.highlightedCity) {
        hashParams.push(`city=${props.highlightedCity.seoName}`);
      }
      if (props.highlightedProfession) {
        hashParams.push(`profession=${props.highlightedProfession.seoName}`);
      }

      return hashParams.length > 0
        ? `${basePath}#${hashParams.join("&")}`
        : basePath;
    });

    const tenderLink = computed(() =>
      localePath({
        name: "requestForTenderTab",
        params: { activeTab: 1 },
        query: { personId: props.person.id },
      })
    );

    const photoCount = computed(
      () => props.person.photoCount || props.person.photos?.length || 0
    );

    const phoneNumber = computed(() => props.person.publicPhoneNumber || null);

    const formattedPhone = computed(() =>
      $helpers.formatPhoneNumber(phoneNumber.value)
    );

    const phoneLink = computed(() =>
      $helpers.getPhoneNumberLink(phoneNumber.value)
    );

    const goToProfile = () => {
      if (listeners.select) {
        emit("select");
        return;
      }
      window.open(professionalLink.value, "_blank", "noopener,noreferrer");
    };

    const onClickPhone = () => {
      if (listeners.callProfessional) {
        emit("callProfessional");
        return;
      }
      if (phoneLink.value) {
        window.location.href = phoneLink.value;
      }
    };

    const onClickCta = (event) => {
      if (listeners.requestTender) {
        event.preventDefault();
        emit("requestTender");
      }
    };

    return {
      isOnlineProfession,
      showInPersonConsultation,
      professionalLink,
      tenderLink,
      photoCount,
      phoneNumber,
      formattedPhone,
      phoneLink,
      hasRequestTenderListener,
      goToProfile,
      onClickPhone,
      onClickCta,
    };
  },
};
</script>

<style scoped>
.picture::v-deep picture {
  width: 100%;
  height: 100%;
}

.picture::v-deep img {
  height: 100%;
  width: 100%;
  object-fit: cover;
}
</style>
